import { useState } from "react";
import { fmtDayNumeric, todayIso } from "../lib/format.js";
import { Segmented } from "./ui.js";
import { NO_AUTOFILL } from "../lib/noAutofill.js";

/**
 * The date filter of the work lists — Captura, Validación, Entrega, Solicitudes and Muestras
 * pendientes (Fernanda, 2026-11-02 20:07: «¿no hay forma de buscar desde una fecha inicial a una
 * fecha final?», after Toronja's «Rango de Fecha»). Presets one tap away, Desde–Hasta always visible (typing a date makes it a free range),
 * and «Todos los días» where the page had it. Dates are lab days (America/Mexico_City), inclusive;
 * the API takes them as `day` or `from`/`to`.
 */

export type DatePreset = "today" | "yesterday" | "week" | "month" | "range" | "any";

export interface DateRange {
  preset: DatePreset;
  /** The free range's ends, YYYY-MM-DD; only read when `preset` is `range`. */
  from: string;
  to: string;
}

/** The query fragment a list endpoint takes for a range: `{ day }`, `{ from, to }` or nothing. */
export function dateRangeQuery(r: DateRange): { day?: string; from?: string; to?: string } {
  const [from, to] = bounds(r);
  if (from === null) return {};
  return from === to ? { day: from } : { from, to };
}

/** First and last lab day of a range; `[null, null]` for every day. */
function bounds(r: DateRange): [string, string] | [null, null] {
  const today = todayIso();
  switch (r.preset) {
    case "today":
      return [today, today];
    case "yesterday":
      return [todayIso(-1), todayIso(-1)];
    case "week":
      return [todayIso(-6), today];
    case "month":
      return [`${today.slice(0, 8)}01`, today];
    case "range":
      return [r.from, r.to];
    case "any":
      return [null, null];
  }
}

/** The range in words, for titles and empty states: "hoy", "ayer", "del 01/10/2026 al 19/10/2026"… */
export function describeDateRange(r: DateRange): string {
  switch (r.preset) {
    case "today":
      return "hoy";
    case "yesterday":
      return "ayer";
    case "week":
      return "en los últimos 7 días";
    case "month":
      return "este mes";
    case "any":
      return "todos los días";
    case "range":
      return r.from === r.to ? `el ${fmtDayNumeric(r.from)}` : `del ${fmtDayNumeric(r.from)} al ${fmtDayNumeric(r.to)}`;
  }
}

/** Range state for a page; every page starts on today. */
export function useDateRange(initial: DatePreset = "today"): [DateRange, (r: DateRange) => void] {
  const [r, setR] = useState<DateRange>(() => ({ preset: initial, from: todayIso(), to: todayIso() }));
  return [r, setR];
}

const PRESETS: { value: DatePreset; label: string }[] = [
  { value: "today", label: "Hoy" },
  { value: "yesterday", label: "Ayer" },
  { value: "week", label: "Últimos 7 días" },
  { value: "month", label: "Este mes" },
];

export function DateRangeFilter({ value, onChange, allowAny = false }: { value: DateRange; onChange: (r: DateRange) => void; allowAny?: boolean }) {
  const today = todayIso();
  const options = allowAny ? [...PRESETS, { value: "any" as const, label: "Todos los días" }] : PRESETS;
  // Desde–Hasta are always on screen (Fernanda, 2026-11-03 14:29: «la búsqueda por fechas que sea
  // como fijo», after Toronja's Fecha inicial / Fecha final). They show the preset's days; typing a
  // date turns the filter into a free range. «Todos los días» leaves them blank.
  const [shownFrom, shownTo] = bounds(value);
  const from = shownFrom ?? today;
  const to = shownTo ?? today;

  function pick(preset: DatePreset) {
    onChange({ preset, from, to });
  }

  // Desde never passes Hasta and Hasta never passes today: moving one end drags the other along,
  // so the range is always valid and the API never sees `to < from`.
  function setFrom(next: string) {
    if (!next) return;
    const f = next > today ? today : next;
    onChange({ preset: "range", from: f, to: to < f ? f : to });
  }
  function setTo(next: string) {
    if (!next) return;
    const t = next > today ? today : next;
    onChange({ preset: "range", from: from > t ? t : from, to: t });
  }

  return (
    <div className="row wrap" style={{ gap: 10 }}>
      <div className="row wrap" style={{ gap: 8 }} role="group" aria-label="Rango de fechas">
        <label className="row" style={{ gap: 6 }}>
          <span className="muted">Desde</span>
          <input {...NO_AUTOFILL} type="date" lang="es-MX" className="input" style={{ width: 170 }} value={shownFrom ?? ""} max={to} onChange={(e) => setFrom(e.target.value)} aria-label="Desde" />
        </label>
        <label className="row" style={{ gap: 6 }}>
          <span className="muted">Hasta</span>
          <input {...NO_AUTOFILL} type="date" lang="es-MX" className="input" style={{ width: 170 }} value={shownTo ?? ""} min={shownFrom ?? undefined} max={today} onChange={(e) => setTo(e.target.value)} aria-label="Hasta" />
        </label>
      </div>
      <Segmented value={value.preset} onChange={pick} options={options} />
    </div>
  );
}
